import { useState, useRef } from 'react';

import api from '@helpers/api.js';

import './styles.scss';

import CustomModal from '@components/CustomModal/index.js';
import { Input, Button, Text } from '@fragments';

import {
    GavelRounded,
    AttachFileRounded,
    PictureAsPdfRounded,
    ImageRounded,
    InsertDriveFileRounded,
    CloseRounded,
    AddRounded,
    DescriptionRounded,
} from '@mui/icons-material';

const MAX_FILES = 10;
// Backend validates ~10MB per document; the total cap keeps the JSON body
// (base64 inflates ~33%) under the server's post-size limit instead of a 413.
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB across all files

const ARGUMENT_OPTIONS = [
    { value: 'cancellation_withdrawn', label: 'Cancelamento retirado pelo cliente' },
    { value: 'refunded', label: 'Reembolso já realizado' },
    { value: 'non_refundable', label: 'Cobrança não reembolsável' },
    { value: 'expired_claim', label: 'Contestação fora do prazo' },
    { value: 'product_as_described', label: 'Produto/serviço conforme descrito' },
    { value: 'credit_provided', label: 'Crédito já concedido ao cliente' },
    { value: 'product_received', label: 'Produto/serviço recebido pelo cliente' },
    { value: 'other', label: 'Outro motivo' },
];

const DOCUMENT_TYPE_OPTIONS = [
    { value: 'receipt', label: 'Comprovante de pagamento' },
    { value: 'invoice', label: 'Nota Fiscal / Fatura' },
    { value: 'contract', label: 'Contrato de adesão' },
    { value: 'proof_of_delivery', label: 'Comprovante de entrega' },
    { value: 'customer_communication', label: 'Conversa com o cliente' },
    { value: 'access_logs', label: 'Logs de acesso / utilização' },
    { value: 'other', label: 'Outro' },
];

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(filename) {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <PictureAsPdfRounded />;
    if (['jpg', 'jpeg', 'png'].includes(ext)) return <ImageRounded />;
    return <InsertDriveFileRounded />;
}

function toBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function DocumentRow({ doc, onRemove, onTypeChange }) {
    return (
        <div className="dispute-modal__file-item">
            <span className="dispute-modal__file-icon">
                {getFileIcon(doc.name)}
            </span>

            <div className="dispute-modal__file-info">
                <Text.span className="dispute-modal__file-name">{doc.name}</Text.span>
                <Text.span contrast={6} className="dispute-modal__file-size">
                    {formatFileSize(doc.size)}
                </Text.span>
            </div>

            <div className="dispute-modal__file-type">
                <Input.Select
                    value={doc.type}
                    onInput={(val) => onTypeChange(doc.id, val)}
                    options={DOCUMENT_TYPE_OPTIONS}
                    placeholder="Tipo de documento"
                    variant="light"
                    start={<Text.span>Tipo</Text.span>}
                />
            </div>

            <button
                type="button"
                className="dispute-modal__file-remove"
                onClick={() => onRemove(doc.id)}
                title="Remover arquivo"
            >
                <CloseRounded style={{ fontSize: 16 }} />
            </button>
        </div>
    );
}

export default function SubmitDisputeModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [argument, setArgument] = useState('');
    const [description, setDescription] = useState('');
    const [documents, setDocuments] = useState([]);
    const [errorTitle, setErrorTitle] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const REF_chargebackId = useRef(null);
    const REF_callback = useRef(null);
    const fileInputRef = useRef(null);

    modalRef.current = {
        open({ chargebackId }, callback) {
            REF_chargebackId.current = chargebackId;
            REF_callback.current = callback || null;
            setArgument('');
            setDescription('');
            setDocuments([]);
            setErrorTitle(null);
            setErrorMessage(null);
            setLoading(false);
            setOpen(true);
        },
        close() {
            setOpen(false);
        },
    };

    async function handleFileSelect(e) {
        const selected = Array.from(e.target.files || []);
        e.target.value = '';

        if (!selected.length) return;

        if (documents.length + selected.length > MAX_FILES) {
            setErrorTitle('Limite de arquivos excedido');
            setErrorMessage(`Você pode anexar no máximo ${MAX_FILES} arquivos.`);
            return;
        }

        const oversized = selected.find((f) => f.size > MAX_FILE_SIZE);
        if (oversized) {
            setErrorTitle('Arquivo muito grande');
            setErrorMessage(`"${oversized.name}" excede o limite de 10 MB por arquivo.`);
            return;
        }

        const currentTotal = documents.reduce((sum, d) => sum + (d.size || 0), 0);
        const selectedTotal = selected.reduce((sum, f) => sum + f.size, 0);
        if (currentTotal + selectedTotal > MAX_TOTAL_SIZE) {
            setErrorTitle('Limite total excedido');
            setErrorMessage(`O conjunto de arquivos não pode ultrapassar ${formatFileSize(MAX_TOTAL_SIZE)}. Remova ou comprima algum documento.`);
            return;
        }

        setErrorTitle(null);
        setErrorMessage(null);

        const newDocs = await Promise.all(
            selected.map(async (file) => ({
                id: `${Date.now()}-${Math.random()}`,
                name: file.name,
                size: file.size,
                type: 'other',
                file_base64: await toBase64(file),
            }))
        );

        setDocuments((prev) => [...prev, ...newDocs]);
    }

    function handleRemoveDocument(id) {
        setDocuments((prev) => prev.filter((d) => d.id !== id));
    }

    function handleDocumentTypeChange(id, type) {
        setDocuments((prev) => prev.map((d) => (d.id === id ? { ...d, type } : d)));
    }

    async function handleSubmit() {
        if (!argument) {
            setErrorTitle('Argumento obrigatório');
            setErrorMessage('Selecione o argumento que melhor representa sua contestação.');
            return;
        }

        if (!description.trim()) {
            setErrorTitle('Descrição obrigatória');
            setErrorMessage('Descreva sua contestação com detalhes para aumentar as chances de sucesso.');
            return;
        }

        if (documents.length === 0) {
            setErrorTitle('Documento obrigatório');
            setErrorMessage('Anexe pelo menos um documento que comprove sua contestação para prosseguir.');
            return;
        }

        setLoading(true);
        setErrorTitle(null);
        setErrorMessage(null);

        try {
            const payload = {
                argument,
                description: description.trim(),
                documents: documents.map(({ type, file_base64 }) => ({ type, file_base64 })),
            };

            await api.post(`charge/chargeback/${REF_chargebackId.current}/dispute`, payload);

            window.setSnackbar('Contestação enviada com sucesso');

            if (REF_callback.current) await REF_callback.current();

            setOpen(false);
        } catch (e) {
            setErrorTitle('Erro ao enviar contestação');
            setErrorMessage(
                e?.response?.data?.message || 'Não foi possível enviar a contestação. Tente novamente.'
            );
        }

        setLoading(false);
    }

    const canAddMore = documents.length < MAX_FILES;
    const descLength = description.length;

    return (
        <CustomModal
            maxWidth={580}
            title="Refutar chargeback"
            bottomButtonSumbitText="Enviar contestação"
            loading={loading}
            open={open}
            handleClose={() => !loading && setOpen(false)}
            handleSubmit={handleSubmit}
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            modalClass="submit-dispute-modal-wrapper"
            bodyComponent={
                <div className="dispute-modal px-4 pt-3 pb-2">

                    {/* Argument */}
                    <div className="dispute-modal__section">
                        <Text.h4
                            weight={400}
                            left={<GavelRounded />}
                            className="dispute-modal__section-title"
                        >
                            Argumento da contestação
                        </Text.h4>
                        <Text.span contrast={6} className="mb-2">
                            Escolha o motivo que melhor justifica a refutação deste pedido de chargeback.
                        </Text.span>
                        <Input.Select
                            className="mt-2"
                            label="Argumento"
                            value={argument}
                            onInput={setArgument}
                            options={ARGUMENT_OPTIONS}
                            placeholder="Selecione um argumento..."
                        />
                    </div>

                    {/* Description */}
                    <div className="dispute-modal__section mt-4">
                        <Text.h4
                            weight={400}
                            left={<DescriptionRounded />}
                            className="dispute-modal__section-title"
                        >
                            Descrição
                        </Text.h4>
                        <Text.span contrast={6} className="mb-2">
                            Explique em detalhes por que este pedido de chargeback não procede.
                        </Text.span>
                        <Input.Text
                            className="mt-2"
                            label="Descreva sua contestação"
                            value={description}
                            onInput={setDescription}
                            multiline
                            size={1000}
                        />
                    </div>

                    {/* Documents */}
                    <div className="dispute-modal__section mt-4">
                        <Text.h4
                            weight={400}
                            left={<AttachFileRounded />}
                            className="dispute-modal__section-title"
                        >
                            Documentos comprobatórios
                        </Text.h4>
                        <Text.span contrast={6} className="mb-2">
                            Anexe arquivos que comprovem sua contestação. PNG, JPG ou PDF · Máximo 50 MB por arquivo · Até {MAX_FILES} arquivos.
                        </Text.span>

                        {documents.length > 0 && (
                            <div className="dispute-modal__file-list mt-2">
                                {documents.map((doc) => (
                                    <DocumentRow
                                        key={doc.id}
                                        doc={doc}
                                        onRemove={handleRemoveDocument}
                                        onTypeChange={handleDocumentTypeChange}
                                    />
                                ))}
                            </div>
                        )}

                        {canAddMore && (
                            <div className="dispute-modal__add-file mt-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.pdf"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                <Button
                                    text={documents.length === 0 ? 'Adicionar documento' : 'Adicionar mais'}
                                    color="white"
                                    size="small"
                                    startIcon={<AddRounded />}
                                    onClick={() => fileInputRef.current?.click()}

                                />
                                {documents.length > 0 && (
                                    <Text.span contrast={7}>
                                        {documents.length} / {MAX_FILES} arquivos
                                    </Text.span>
                                )}
                            </div>
                        )}
                    </div>

                </div>
            }
        />
    );
}
